import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './TermsOfService.module.css';

const TermsOfService = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <b className={css.lastUpdated}>Effective: 24 September, 2022</b><br/>
      <b className={css.lastUpdated}>Last updated: 3 December, 2025</b>
      <p>
        These User Terms of Service (“<strong>User Terms</strong>”) govern your access to and use of
        our online marketplace for coaching and leadership services, including our website, mobile
        application and related services (collectively, the “<strong>Services</strong>”). Please
        read them carefully.
      </p>

      <p>By accessing or using the Services, you agree to be bound by these User Terms.</p>

      <p>
        These User Terms form a legally binding contract between you and{' '}
        <strong>Bendelta Pty Ltd</strong> (“<strong>Bendelta</strong>”, “<strong>we</strong>”, “
        <strong>us</strong>”, “<strong>our</strong>”), trading as <strong>BOLDLY</strong>, with the
        following contact details:
      </p>

      <blockquote>
        <p>
          <strong>Address: </strong>
          Level 5, 32–36 York Street, Sydney NSW 2000, Australia
          <br />
          <strong>Phone:</strong> +61 2 9232 3401
          <br />
          Email:{' '}
          <a href="mailto:connect@boldly.app">
            <strong>connect@boldly.app</strong>
          </a>
        </p>
      </blockquote>

      <h2>1. Scope and Acceptance</h2>

      <p>
        1.1 These User Terms apply to all individual users of the Services, including “Authorised
        Users” as defined below.
      </p>

      <p>1.2 By accessing or using the Services, you:</p>
      <ul>
        <li>
          a. confirm that you have read, understood and agree to be bound by these User Terms; and
          <br />
        </li>
        <li>
          b. agree to comply with our <strong>Acceptable Use Policy</strong> and{' '}
          <strong>Privacy Policy</strong>, which are incorporated into these User Terms by
          reference.
        </li>
      </ul>
      <p>1.3 If you do not agree to these User Terms, you must not access or use the Services.</p>

      <h2>2. Key Parties and Definitions</h2>
      <p></p>
      <ul>
        <li>
          <strong>Customer</strong>: An organisation, association or individual that enters into a
          separate agreement with us for access to the Services (a “Customer Contract”), and through
          which you are granted access as an Authorised User.
        </li>
        <li>
          <strong>Authorised User / you</strong>: An individual user who is granted access to the
          Services by a Customer or who has signed up directly and is therefore both a Customer and
          an Authorised User.
        </li>
        <li>
          <strong>Customer Data</strong>: Any content, data, files, information, messages or
          materials submitted to the Services by or on behalf of a Customer or its Authorised Users
          in connection with that Customer’s use of the Services.
        </li>
        <li>
          <strong>User Content</strong>: Any content you submit to the Services in your personal
          capacity (for example, your profile, feedback, reviews, comments, uploads, and any
          materials that are not clearly Customer Data).
        </li>
      </ul>

      <h2>3. Relationship Between Customer, Authorised Users and Bendelta</h2>

      <p>
        3.1 If you access the Services because your employer, association or other organisation has
        provisioned an account for you, then that entity is the <strong>Customer</strong>, and you
        are an <strong>Authorised User</strong> of that Customer.
      </p>

      <p>
        3.2 Where you sign up individually without a separate Customer Contract, you are both the{' '}
        <strong>Customer</strong> and the <strong>Authorised User</strong>, and these User Terms
        (together with our Privacy Policy and any onboarding information) form the agreement between
        you and Bendelta.
      </p>

      <p>
        3.3 As between Bendelta and the Customer, the{' '}
        <strong>Customer controls its instance</strong> of the Services and Customer Data. The
        Customer may:
      </p>
      <ul>
        <li>a. provision, manage and deprovision Authorised Users;</li>
        <li>b. configure settings (including retention, access and export settings);</li>
        <li>c. enable or disable integrations and features; and</li>
        <li>d. access, export, modify or delete Customer Data.</li>
      </ul>

      <p>
        3.4 You acknowledge that <strong>Customer Data is owned by the Customer</strong>, and
        Bendelta processes Customer Data on behalf of the Customer in accordance with the applicable
        Customer Contract and our Privacy Policy.
      </p>

      <p>
        3.5 As between Bendelta and the Customer, it is solely the Customer’s responsibility to:
      </p>
      <ul>
        <li>a. inform you of any Customer policies relevant to your use of the Services;</li>
        <li>
          b. obtain any rights, permissions or consents from you that are necessary for the lawful
          use of Customer Data; and
        </li>
        <li>
          c. respond to and resolve any dispute with you relating to Customer Data or the Customer’s
          use of the Services.
        </li>
      </ul>

      <h2>4. Access to the Services</h2>

      <p>
        4.1 Subject to your ongoing compliance with these User Terms, Bendelta grants you a{' '}
        <strong>limited, revocable, non-exclusive, non-transferable</strong> right to access and use
        the Services for their intended purpose.
      </p>

      <p>
        4.2 If you access the Services via a <strong>mobile application (App)</strong>, Bendelta
        grants you a limited, non-exclusive, non-transferable licence to download, install and use
        the App on a mobile device you own or control, solely for access to the Services in
        accordance with these User Terms.
      </p>

      <p>4.3 You are responsible for:</p>
      <ul>
        <li>a. maintaining the confidentiality of your login credentials;</li>
        <li>b. all activities that occur under your account; and</li>
        <li>
          c. ensuring that your device(s), operating system and internet connection are suitable for
          accessing the Services.
        </li>
      </ul>

      <p>
        4.4 If you access the Services on a mobile device, your carrier’s usual charges, data rates
        and fees may apply. Some functionality may not work with all devices or carriers.
      </p>

      <h2>5. Eligibility</h2>

      <p>
        5.1 The Services are intended for <strong>users aged 18 or over</strong>. By using the
        Services, you represent and warrant that you are at least 18 years old and have the legal
        capacity to enter into these User Terms.
      </p>

      <p>
        5.2 You must also be of <strong>legal working age</strong> in the jurisdiction in which you
        are using the Services.
      </p>

      <h2>6. Acceptable Use</h2>

      <p>6.1 You must not use the Services in any manner that:</p>
      <ul>
        <li>a. is unlawful, fraudulent, misleading or deceptive;</li>
        <li>b. infringes any intellectual property, privacy or other rights of any person;</li>
        <li>c. involves harassment, discrimination, hate speech, threats or abuse;</li>
        <li>
          d. interferes with or disrupts the operation of the Services or any systems or networks;
        </li>
        <li>e. attempts to bypass or undermine security or authentication measures; or</li>
        <li>f. otherwise violates our Acceptable Use Policy.</li>
      </ul>

      <p>
        6.2 You agree to remain vigilant and to promptly report any inappropriate behaviour, content
        or suspected misuse of the Services to both the Customer (where applicable) and Bendelta at{' '}
        <a href="mailto:connect@boldly.app">
          <strong>connect@boldly.app</strong>
        </a>
        .
      </p>

      <h2>7. User Content and Marketplace Conduct</h2>

      <p>
        7.1 The Services enable users (including coaches and clients) to create profiles, share
        information, leave feedback, upload materials and interact with one another. This is “
        <strong>User Content</strong>”.
      </p>

      <p>7.2 By submitting User Content, you:</p>
      <ul>
        <li>
          a. represent and warrant that you have all necessary rights to do so and that the content
          is accurate, lawful and not misleading;
        </li>
        <li>
          b. grant Bendelta a worldwide, non-exclusive, royalty-free, transferable licence (with the
          right to sub-license) to use, host, reproduce, modify, adapt, publish, translate, create
          derivative works from, distribute, display and perform that User Content in connection
          with operating, improving and promoting the Services; and
        </li>
        <li>
          c. acknowledge that User Content may be visible to other users of the Services, subject to
          the Customer’s settings and your own choices.
        </li>
      </ul>

      <p>
        7.3 You are solely responsible for your User Content and for any interactions you have with
        other users (including coaches and clients). Bendelta does not endorse, verify or guarantee
        any User Content or user-to-user interactions.
      </p>

      <p>
        7.4 Bendelta may, but is not obliged to, monitor or review User Content. We may remove,
        restrict or disable access to any User Content (or your account) at our discretion where we
        reasonably believe there has been a breach of these User Terms, our Acceptable Use Policy,
        or applicable law.
      </p>

      <h2>8. Customer Data vs User Content</h2>

      <p>
        8.1 Where you are an Authorised User of a Customer, content you submit{' '}
        <strong>within that Customer’s instance</strong> (including coaching notes, communications
        and uploads for that Customer’s engagement) will generally be treated as{' '}
        <strong>Customer Data</strong> and controlled by the Customer.
      </p>

      <p>
        8.2 Content you provide in your own profile or in a personal capacity on the marketplace
        (e.g. coach bios, public reviews, testimonials) may be treated as{' '}
        <strong>User Content</strong>.
      </p>

      <p>
        8.3 Our Privacy Policy explains how we handle both Customer Data and User Content, including
        cross-border transfers.
      </p>

      <h2>9. Intellectual Property</h2>

      <p>
        9.1 The Services, including all underlying software, technology, databases, user interfaces,
        designs, text, graphics, logos, icons, images, audio, video and compilations, are owned by
        Bendelta or its licensors and are protected by Australian and international intellectual
        property laws.
      </p>

      <p>9.2 Except as expressly authorised in these User Terms, you must not:</p>
      <ul>
        <li>
          a. copy, reproduce, modify, adapt, translate, create derivative works from, distribute,
          transmit, display, perform or otherwise exploit any part of the Services or their content;
        </li>
        <li>
          b. reverse engineer, decompile or attempt to extract source code (except to the extent
          permitted by law); or
        </li>
        <li>c. remove or obscure any proprietary or copyright notices.</li>
      </ul>

      <h2>10. Copyright Infringement and Takedown (Australia)</h2>

      <p>10.1 Bendelta respects intellectual property rights and expects users to do the same.</p>

      <p>
        10.2 If you believe any material on the Services infringes your copyright, please notify us
        at:
      </p>

      <blockquote>
        <p>
          <strong>Email</strong>:{' '}
          <a href="mailto:connect@boldly.app">
            <strong>connect@boldly.app</strong>
          </a>
          <br />
          <strong>Postal</strong>: Bendelta Pty Ltd – Copyright Notice
          <br />
          Level 5, 32–36 York Street, Sydney NSW 2000, Australia
        </p>
      </blockquote>

      <p>Your notice should include:</p>
      <ul>
        <li>a. identification of the copyrighted work claimed to have been infringed;</li>
        <li>
          b. identification of the material you claim is infringing (e.g. URL or description);
        </li>
        <li>c. your name, postal address, telephone number and email address;</li>
        <li>d. a statement that you believe in good faith that the use is not authorised; and</li>
        <li>
          e. a statement that the information in your notice is accurate, and that you are the
          copyright owner or are authorised to act on the owner’s behalf.
        </li>
      </ul>

      <p>
        10.3 We may remove or disable access to the material while we review your notice, and may
        contact the uploader for a response.
      </p>

      <h2>11. Trademarks</h2>

      <p>
        11.1 “BOLDLY”, “momentLeader” and any related logos, graphics, page headers, button icons,
        scripts and service names are trademarks or trade dress of Bendelta or its affiliates.
      </p>

      <p>
        11.2 You must not use these marks in connection with any product or service that is not
        Bendelta’s, or in any way that may cause confusion or disparage Bendelta.
      </p>

      <h2>12. Coaching Engagements and Honor Code</h2>

      <p>12.1 If you engage in coaching or mentoring via the Services, you agree to:</p>
      <ul>
        <li>a. complete any coaching assessments and tasks personally and honestly;</li>
        <li>
          b. provide at least <strong>24 hours’ notice</strong> if you need to reschedule a session,
          subject to any Customer’s or coach’s cancellation policy;
        </li>
        <li>c. provide constructive feedback to your coach; and</li>
        <li>
          d. raise any concerns or dissatisfaction with your coach in the first instance and, if
          unresolved, escalate to Bendelta at{' '}
          <a href="mailto:connect@boldly.app">
            <strong>connect@boldly.app</strong>
          </a>
          .
        </li>
      </ul>

      <p>
        12.2 Coaches using the platform agree to act professionally, maintain confidentiality in
        accordance with applicable laws and contractual obligations, and comply with any
        professional or ethical standards applicable to their coaching practice.
      </p>

      <h2>13. Term, Suspension and Termination (Authorised Users)</h2>

      <p>
        13.1 These User Terms remain in effect for as long as you access or use the Services as an
        Authorised User.
      </p>

      <p>13.2 Your access may end when:</p>
      <ul>
        <li>a. the relevant Customer Contract terminates or expires;</li>
        <li>b. the Customer removes your access; or</li>
        <li>
          c. Bendelta suspends or terminates your access due to breach of these User Terms, our
          Acceptable Use Policy, or applicable law, or where continued access poses a risk to the
          Services, other users or third parties.
        </li>
      </ul>

      <p>
        13.3 We may suspend or restrict your access on a temporary basis where we reasonably suspect
        misuse or security risk, while we investigate.
      </p>

      <h2>14. Fees and Payment (Customers)</h2>

      <p>
        14.1 If you are a Customer, fees, billing cycles, taxes and payment methods are governed by
        your Customer Contract. In general:
      </p>
      <ul>
        <li>
          a. fees are payable in advance and are non-refundable, except as required by law or
          expressly stated in the Customer Contract;
        </li>
        <li>b. late payments may attract interest and/or suspension of the Services; and</li>
        <li>
          c. you are responsible for all applicable taxes in connection with your use of the
          Services.
        </li>
      </ul>

      <p>
        14.2 If you are only an Authorised User (and not the Customer), you are not directly
        responsible to Bendelta for payment of fees, unless otherwise agreed.
      </p>

      <h2>15. Warranties and Disclaimers</h2>

      <p>15.1 While we aim to provide a reliable and secure platform, you acknowledge that:</p>
      <ul>
        <li>
          a. the Services are provided on an “<strong>as is</strong>” and “
          <strong>as available</strong>” basis;
        </li>
        <li>
          b. we do not guarantee that the Services will be uninterrupted, error-free or free from
          harmful components; and
        </li>
        <li>
          c. we do not control and are not responsible for the conduct, actions, advice,
          representations or competence of any coach, client or other user.
        </li>
      </ul>

      <p>
        15.2 To the maximum extent permitted by law, Bendelta disclaims all warranties, whether
        express, implied or statutory, including any implied warranties of merchantability, fitness
        for a particular purpose, title and non-infringement.
      </p>

      <p>
        15.3 Nothing in these User Terms restricts any rights or remedies you may have under the{' '}
        <strong>Australian Consumer Law</strong> that cannot be excluded.
      </p>

      <h2>16. Limitation of Liability</h2>

      <p>
        16.1 To the maximum extent permitted by law, Bendelta and its directors, officers,
        employees, contractors and agents will not be liable for:
      </p>
      <ul>
        <li>
          a. any indirect, incidental, consequential, special, exemplary or punitive loss or damage;
          or
        </li>
        <li>
          b. loss of profit, revenue, goodwill, opportunity, data or business interruption,
          <br />
          arising out of or in connection with the Services or these User Terms, even if advised of
          the possibility of such loss.
        </li>
      </ul>

      <p>
        16.2 To the maximum extent permitted by law, Bendelta’s <strong>aggregate liability</strong>{' '}
        to you arising out of or in connection with the Services or these User Terms is limited to{' '}
        <strong>AUD $150</strong>.
      </p>

      <p>
        16.3 Nothing in these User Terms limits or excludes liability that cannot be limited or
        excluded under applicable law, including liability for death or personal injury caused by
        negligence, or for breach of certain statutory guarantees under the Australian Consumer Law.
      </p>

      <h2>17. Indemnity (Business Users)</h2>

      <p>
        17.1 If you use the Services in connection with a business, you indemnify Bendelta and its
        officers, employees and agents from and against any loss, liability, damage, cost or expense
        (including reasonable legal fees) arising from or in connection with:
      </p>
      <ul>
        <li>a. your breach of these User Terms or the Acceptable Use Policy;</li>
        <li>b. your User Content; or</li>
        <li>c. your misuse of the Services.</li>
      </ul>

      <h2>18. Privacy and Data Protection</h2>

      <p>
        18.1 Our <strong>Privacy Policy</strong> explains how we collect, use, store and disclose
        personal information, including Customer Data and User Content, and how we handle
        cross-border transfers.
      </p>

      <p>
        18.2 By using the Services, you consent to the collection and use of your personal
        information in accordance with our Privacy Policy.
      </p>

      <h2>19. Modifications to These User Terms</h2>

      <p>
        19.1 We may update these User Terms from time to time. If we make material changes, we will
        provide you with reasonable notice (for example, by email or an in-app notification) before
        the changes take effect.
      </p>

      <p>
        19.2 The updated User Terms will be published on our website. Your continued use of the
        Services after the changes take effect constitutes your acceptance of the updated User
        Terms.
      </p>

      <h2>20. Assignment</h2>

      <p>
        20.1 You may not assign or transfer your rights or obligations under these User Terms
        without our prior written consent.
      </p>

      <p>
        20.2 We may assign or transfer our rights and obligations under these User Terms, in whole
        or in part, to:
      </p>
      <ul>
        <li>a. a related body corporate; or</li>
        <li>
          b. a successor or purchaser in connection with a merger, acquisition, corporate
          reorganisation or sale of all or substantially all of our assets.
        </li>
      </ul>

      <h2>21. Governing Law and Jurisdiction</h2>

      <p>
        21.1 These User Terms are governed by the laws of{' '}
        <strong>New South Wales, Australia</strong>.
      </p>

      <p>
        21.2 The parties submit to the{' '}
        <strong>non-exclusive jurisdiction of the courts of New South Wales, Australia</strong> and
        any courts that may hear appeals from those courts in respect of any proceedings arising out
        of or in connection with these User Terms or the Services.
      </p>

      <p>
        21.3 In any action or proceeding to enforce these User Terms, the prevailing party is
        entitled to recover its reasonable legal costs and expenses.
      </p>

      <h2>22. General</h2>

      <p>
        22.1 <strong>Notices</strong>
        <br />
        Notices to Bendelta should be sent to{' '}
        <a href="mailto:connect@boldly.app">
          <strong>connect@boldly.app</strong>
        </a>
        . We may send notices to you via email or through the Services.
      </p>

      <p>
        22.2 <strong>Waiver</strong>
        <br />
        No failure or delay in exercising any right or remedy under these User Terms constitutes a
        waiver of that right or remedy. A waiver must be in writing and signed by the party granting
        it.
      </p>

      <p>
        22.3 <strong>Severability</strong>
        <br />
        If any provision of these User Terms is held invalid, illegal or unenforceable, that
        provision will be enforced to the maximum extent permissible and the remaining provisions
        will continue in full force and effect.
      </p>

      <p>
        22.4 <strong>Entire Agreement</strong>
        <br />
        These User Terms, together with the Acceptable Use Policy, Privacy Policy and any applicable
        Customer Contract (where you are a Customer or Authorised User), constitute the entire
        agreement between you and Bendelta regarding your use of the Services and supersede all
        prior or contemporaneous understandings, agreements or communications.
      </p>

      <h2>23. Contact</h2>

      <p>If you have any questions about these User Terms or the Services, please contact us at:</p>

      <blockquote>
        <p>
          <strong>Bendelta Pty Ltd (trading as BOLDLY)</strong>
          <br />
          Level 5, 32–36 York Street
          <br />
          Sydney NSW 2000, Australia
          <br />
          Phone: +61 2 9232 3401
          <br />
          Email:{' '}
          <a href="mailto:connect@boldly.app">
            <strong>connect@boldly.app</strong>
          </a>
        </p>
      </blockquote>
    </div>
  );
};

TermsOfService.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

TermsOfService.propTypes = {
  rootClassName: string,
  className: string,
};

export default TermsOfService;
